import type { Json, MergeStatusRow } from '../../lib/database.types'
import { clock } from '../../lib/time'

/**
 * Which open pull requests can be merged, as the `merge-status` job last
 * worked it out.
 *
 * **This module does not judge a pull request**, the same boundary
 * `fleetStatus.ts` holds for a job's health. The job on the box asks GitHub
 * and Linear every quarter hour, decides ready / building / not ready, writes
 * the reason and the post-merge steps, and this arranges what it wrote.
 * Re-deriving "ready" here from `ci` and `review_state` would be a second
 * implementation of a judgement the fleet already made, and the two would
 * eventually disagree in front of the person about to press Merge.
 *
 * `merge_status` is keyed by `key` and holds one pass at a time, so every row
 * of a pass carries the same `generated_at`. That timestamp is the page's own
 * freshness rather than GitHub's: a merge-status job that has stopped running
 * has to look different from a fleet with nothing to merge, which is the move
 * the Status panel and the Directory both make.
 */

/**
 * How old the newest pass may be before the panel stops claiming to know.
 *
 * The job's timer is every fifteen minutes, so this is two missed passes -- a
 * real outage rather than one slow run or a reboot, and the figure the ticket
 * names.
 */
export const MERGE_STALE_MS = 30 * 60 * 1000

/**
 * What a column the job could not fill reads as.
 *
 * Every one of CI, mergeable, review and reviewer is nullable, and null means
 * the job could not find out -- unreadable checks, a pull request GitHub would
 * not answer for. It says so in that word rather than rendering an empty cell,
 * because a blank beside "Ready" reads as nothing to report, which is the one
 * thing it is not.
 */
export const UNKNOWN = 'unknown'

/**
 * The verdicts the job writes, in the order a person should read them, with
 * what to call one and which state colour it takes.
 *
 * Ready first because it is what the page is opened for, then Building --
 * which will resolve itself -- then the ones needing a person. A verdict not
 * in this table renders as its own value, sorts after all of them and is
 * counted under its own name, the way the activity feed renders an
 * unregistered event type rather than hiding it. Guessing "ready" about a word
 * we do not know is the one answer that gets something merged.
 */
const VERDICTS: ReadonlyArray<{ value: string; label: string; token: string }> = [
  { value: 'ready', label: 'Ready', token: 'done' },
  { value: 'building', label: 'Building', token: 'run' },
  { value: 'not_ready', label: 'Not ready', token: 'fail' },
]

/**
 * `ticket_ref` is a bare `DEV-311` -- no column carries a ticket URL for a
 * pull request row, and Linear resolves an issue link without its title slug
 * (the same slugless form `v_findings.linear_url` already renders).
 */
const LINEAR_ISSUE = 'https://linear.app/98-holdings/issue/'

export interface MergePull {
  key: string
  /** "98-Ventures/98h-fleet-dashboard#53", or whatever of it the row carried. */
  label: string
  url: string | null
  title: string | null
  ticketRef: string | null
  ticketUrl: string | null
  ci: string
  mergeable: string
  reviewState: string
  /** The reviewer agent's verdict, or `attended` for a pull a person built. */
  reviewer: string
  /** The raw column value, which is what an unrecognised verdict renders as. */
  verdict: string
  verdictLabel: string
  /** A `--s-*` token name, for the one word of colour a row carries. */
  token: string
  /** Why it is not ready. The job leaves it null when it is. */
  reason: string | null
  /** Ready-made sentences, rendered as text. Nothing here interprets one. */
  postMerge: string[]
}

export interface MergeQueueable {
  key: string
  /** The ticket's identifier; the row's key if it somehow carried none. */
  ref: string
  title: string | null
  url: string | null
}

export interface MergePanel {
  /** When the newest pass ran, or null if the table holds nothing at all. */
  generatedAt: string | null
  /** The newest pass is older than the job's timer allows for. */
  stale: boolean
  /** "Merge status job down since 09:42", or null while it is reporting. */
  downSince: string | null
  /** Ready, then building, then not ready, then anything unrecognised. */
  pulls: MergePull[]
  /** Backlog tickets whose blockers are all done. */
  queueable: MergeQueueable[]
  /** "Updated 09:42 · 2 ready · 3 not ready · 1 building". */
  headline: string
}

function known(verdict: string) {
  return VERDICTS.find((v) => v.value === verdict) ?? null
}

/** A column the job filled, or the word for one it could not. */
function filled(value: string | null): string {
  const text = value?.trim()
  return text ? text : UNKNOWN
}

/**
 * The post-merge steps, as text.
 *
 * The job writes a flat array of finished sentences ("apply 0003_x.sql to
 * 98h-finance"), so there is nothing to interpret and this does not try. An
 * entry that is not a string is still shown -- as its own JSON -- because a
 * step the page silently dropped is a migration nobody applies.
 */
function steps(value: Json | null): string[] {
  if (!Array.isArray(value)) return []
  const out: string[] = []
  for (const entry of value) {
    const text = typeof entry === 'string' ? entry.trim() : entry === null ? '' : JSON.stringify(entry)
    if (text) out.push(text)
  }
  return out
}

function toPull(row: MergeStatusRow): MergePull {
  const repo = row.repo?.trim() ?? ''
  const number = row.pr_number === null ? '' : `#${row.pr_number}`
  const ticketRef = row.ticket_ref?.trim() || null
  const verdict = filled(row.verdict)
  const match = known(verdict)
  return {
    key: row.key,
    label: `${repo}${number}` || row.key,
    url: row.url?.trim() || null,
    title: row.title?.trim() || null,
    ticketRef,
    ticketUrl: ticketRef ? `${LINEAR_ISSUE}${encodeURIComponent(ticketRef)}` : null,
    ci: filled(row.ci),
    mergeable: filled(row.mergeable),
    reviewState: filled(row.review_state),
    reviewer: filled(row.reviewer),
    verdict,
    verdictLabel: match?.label ?? verdict,
    token: match?.token ?? 'queue',
    reason: row.reason?.trim() || null,
    postMerge: steps(row.post_merge),
  }
}

function toQueueable(row: MergeStatusRow): MergeQueueable {
  return {
    key: row.key,
    ref: row.ticket_ref?.trim() || row.key,
    title: row.title?.trim() || null,
    url: row.url?.trim() || null,
  }
}

/** Ready first, in the table's order; an unknown verdict sorts after all of them. */
function rank(verdict: string): number {
  const at = VERDICTS.findIndex((v) => v.value === verdict)
  return at === -1 ? VERDICTS.length : at
}

/**
 * The counts, in the order the ticket writes them -- which is not the order
 * the rows are in, because the header is read as a sentence ("two ready, three
 * not") and the list is read worst-last.
 *
 * All three are printed even at zero: the claim of the line is that the job
 * looked and found none, and a header that drops them reads the same whether
 * the count is nought or the panel is broken. Anything the table does not know
 * is named by its own value after them rather than swept into a total that
 * would make it look accounted for.
 */
function tally(pulls: ReadonlyArray<MergePull>): string {
  const n = (value: string) => pulls.filter((p) => p.verdict === value).length
  const parts = [`${n('ready')} ready`, `${n('not_ready')} not ready`, `${n('building')} building`]
  const unknown = new Map<string, number>()
  for (const p of pulls) if (!known(p.verdict)) unknown.set(p.verdict, (unknown.get(p.verdict) ?? 0) + 1)
  for (const [value, c] of unknown) parts.push(`${c} ${value}`)
  return parts.join(' · ')
}

/**
 * @param rows  whatever the table held, which is one pass in the ordinary case
 * @param now   the clock, injectable so staleness is testable
 */
export function toMergePanel(rows: ReadonlyArray<MergeStatusRow>, now: number = Date.now()): MergePanel {
  // The newest stamp rather than any row's: the pass is written row by row and
  // the stale ones deleted after, so a read landing mid-write sees two passes
  // at once. Showing only the newest means a pull request appears once, at the
  // verdict the job most recently gave it, rather than twice at two.
  let generatedAt: string | null = null
  for (const r of rows) if (generatedAt === null || r.generated_at > generatedAt) generatedAt = r.generated_at
  const latest = rows.filter((r) => r.generated_at === generatedAt)

  // Array.prototype.sort is stable, so within one verdict the rows keep the
  // order the job wrote them in -- by repository and then pull number. That is
  // the job's ordering and not a second one invented here.
  const pulls = latest.filter((r) => r.kind === 'pr').map(toPull).sort((a, b) => rank(a.verdict) - rank(b.verdict))
  const queueable = latest.filter((r) => r.kind === 'queueable').map(toQueueable)

  const at = generatedAt ? Date.parse(generatedAt) : NaN
  // An empty table is day one, not an outage: before the job's first pass there
  // is nothing to be stale, and saying "down" about a job that has never run
  // would be an assertion nothing supports. The view renders that case as an
  // empty panel instead.
  const stale = generatedAt !== null && (Number.isNaN(at) || now - at > MERGE_STALE_MS)

  return {
    generatedAt,
    stale,
    downSince: stale && generatedAt ? `Merge status job down since ${clock(generatedAt)}` : null,
    pulls,
    queueable,
    headline: `${generatedAt ? `Updated ${clock(generatedAt)}` : 'Never updated'} · ${tally(pulls)}`,
  }
}

/**
 * The panel as the Merge view builds it, from a query result rather than from
 * rows -- because *which clock* the staleness check reads is the whole of the
 * banner, and DEV-308 settled it for the Status panel.
 *
 * `toMergePanel`'s default clock is the moment it is called, and that is the
 * render clock. React Query hands back the same `data` reference when a poll
 * brings nothing new, so a stopped job produces polls that change nothing, no
 * re-render, no recompute, and a banner that never fires on the tab that
 * needed it. `dataUpdatedAt` is restamped on every successful fetch whether or
 * not the rows moved, so it advances exactly when the page has re-confirmed
 * what it is showing -- which is the claim the banner makes.
 */
export function mergePanelFrom(
  read: { data?: ReadonlyArray<MergeStatusRow>; dataUpdatedAt: number },
): MergePanel {
  return toMergePanel(read.data ?? [], read.dataUpdatedAt || Date.now())
}
